import React, { useState, useRef, useEffect, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { Handle, Position } from 'reactflow';
import { Sparkles, AlertCircle, ChevronDown, ChevronRight } from 'lucide-react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowUpRightFromSquare, faCheck, faLink, faPen, faTags } from '@fortawesome/free-solid-svg-icons';
import { format } from 'date-fns';
import { AutomationActionNodeSubType, AutomationNodeType } from '@frontend/app/types/globalTypes';
import { WorkletSpecUri } from '@frontend/app/containers/Projects/constants';
import { useAuth } from '@frontend/context/authContext';
import { useGetAllWorkletsForParentSpecQuery } from '@frontend/app/hooks';
import { SALES_APP_ID } from '@frontend/app/constants/applicationIds';
import { useApolloClient } from '@frontend/applications/AffiliatesApp/hooks';
import { GET_OFFERS_QUERY, GET_OFFER_BY_ID_WITH_STATS_QUERY } from '@frontend/applications/AffiliatesApp/queries';
import { GetOffers_offers } from '@frontend/applications/AffiliatesApp/queries/types/GetOffers';
import {
  GetofferByIdWithStats,
  GetofferByIdWithStatsVariables,
} from '@frontend/applications/AffiliatesApp/queries/types/GetofferByIdWithStats';
import { logger } from '@common';
import { getPayoutLabel } from '@frontend/applications/AffiliatesApp/components/OfferForm/utils/OfferFormUtils';
import { useAppContext } from '@frontend/context/AppContext';
import { GET_BRAND_CATALOGS_QUERY } from '@frontend/applications/ProductFulfillmentApp/queries/brandCatalog';
import { GetBrandCatalogsQuery } from '@frontend/applications/ProductFulfillmentApp/queries/types/GetBrandCatalogsQuery';
import { GET_SELECTION_CRITERIA } from '@frontend/applications/ProductFulfillmentApp/queries/selectionCriteria';
import {
  GetCatalogSelectionRuleByCatalogId,
  GetCatalogSelectionRuleByCatalogIdVariables,
} from '@frontend/applications/ProductFulfillmentApp/queries/types/GetCatalogSelectionRuleByCatalogId';
import { GetNodeOptions_getNodeOptions_actionOptions } from '@frontend/app/queries/types/GetNodeOptions';
import { tagColors } from '@frontend/app/hooks';
import { simpleHash } from '@frontend/app/containers/Settings/ProductFulfillment/utils';
import { Spinner } from '@revfluence/fresh';
import { NodeToolbarV2 } from './shared/NodeToolbarV2';
import { EmptyStateButton } from './shared/EmptyStateButton';
import { Button } from '@/shadcn/components/ui/button';
import { Badge } from '@/shadcn/components/ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from '@/shadcn/components/ui/popover';
import { Command, CommandGroup, CommandInput, CommandItem, CommandList } from '@/shadcn/components/ui/command';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/shadcn/components/ui/tooltip';
// Tooltip components now handled by EmptyStateButton
import { FlowNodeData } from '../types';
import { cn } from '@/shadcn/lib/utils';
import { getOfferDiscountAmount, getOfferIconData } from '../utils/automationTreeUtils';
import { UPDATE_MEMBER_DATA_TYPES } from '../PropertyPanel/ActionTypes/UpdateMemberDataActionForm';
import { NodeIcon, getNodeDisplayLabel, getNodeIconConfig } from '../utils/nodeIconMapping';
import { Card, CardContent } from '@/shadcn/components/ui/card';
import DeleteNodeButton from './shared/DeleteNodeButton';
import AddNodeButton from './shared/AddNodeButton';

interface ActionNodeV2Props {
  data: FlowNodeData;
}

// Interface for action items in the dropdown
interface ActionItem {
  id: string;
  iconComponent: React.ComponentType<{ className?: string }>;
  label: string;
  bgColor: string;
  isDisabled: boolean;
  parentType?: AutomationActionNodeSubType;
  memberDataType?: string;
}

const getActionDescription = (
  subType?: AutomationActionNodeSubType,
  metadata?: any,
  actionOptions?: GetNodeOptions_getNodeOptions_actionOptions[],
  catalogName?: string | null,
) => {
  switch (subType) {
    case AutomationActionNodeSubType.SEND_EMAIL:
      if (metadata?.templateId && actionOptions?.length > 0) {
        // Find template name from actionOptions
        const template = actionOptions[0]?.messageTemplates?.find((t) => t.id === metadata.templateId);
        return template?.displayName || 'Email template configured';
      }
      return 'Send an email to the member';
    case AutomationActionNodeSubType.HANDLE_PFA_BOOST:
      return metadata?.boostId ? `Boost ID: ${metadata.boostId}` : 'Apply performance boost';
    case AutomationActionNodeSubType.HANDLE_CASHBACK_BOOST:
      return 'Apply cashback boost to member';
    case AutomationActionNodeSubType.UPDATE_MEMBER_DATA:
      if (metadata?.type) {
        // Handle multi-tag format
        if (metadata.type === UPDATE_MEMBER_DATA_TYPES.ADD_TAG && metadata.values && metadata.values.length > 0) {
          if (metadata.values.length === 1) {
            return `Add Tag: ${metadata.values[0].fieldValue}`;
          } else {
            return `Add Tags: ${metadata.values.map((v: any) => v.fieldValue).join(', ')}`;
          }
        } else if (metadata?.fieldValue) {
          const actionType = metadata.type === 'TAG' ? 'Add Tag' : 'Add to Group';
          return `${actionType}: ${metadata.fieldValue}`;
        }
      }
      return 'Update member data';
    case AutomationActionNodeSubType.ADD_TO_OFFER:
      return metadata?.offerId ? `Add to Offer ID: ${metadata.offerId}` : 'Add member to an offer';
    case AutomationActionNodeSubType.SEND_PRODUCT_CATALOG:
      if (catalogName) {
        return `Catalog: ${catalogName}`;
      }
      return metadata?.brandCatalogId ? `Catalog ID: ${metadata.brandCatalogId}` : 'Send product catalog to member';
    default:
      return 'Perform an action';
  }
};

export const ActionNodeV2: React.FC<ActionNodeV2Props> = ({ data }) => {
  const { clientInfo } = useAuth();
  const { pfaApolloClient } = useAppContext();
  const {
    nodeId,
    subType,
    metadata,
    isEditing,
    isSelected,
    onNodeClick,
    onUpdateNode,
    onDeleteNode,
    onNodeResize,
    actionOptions,
    programData,
    isStaOffer,
  } = data;
  const [showOptionsPopover, setShowOptionsPopover] = useState(false);
  const [isActionSelectorOpen, setIsActionSelectorOpen] = useState(false);
  const [emailAccordionExpanded, setEmailAccordionExpanded] = useState(false);
  const popoverRef = useRef<HTMLDivElement>(null);

  // Height measurement for dynamic spacing
  const nodeRef = useRef<HTMLDivElement>(null);

  // Add states for offer details
  const [selectedOffer, setSelectedOffer] = useState<GetOffers_offers | null>(null);
  const [selectedOfferDetails, setSelectedOfferDetails] = useState<GetofferByIdWithStats['offer'] | null>(null);
  const [payoutLabel, setPayoutLabel] = useState<string | null>(null);
  const staApolloClient = useApolloClient();

  // Add states for catalog details
  const [catalogName, setCatalogName] = useState<string | null>(null);
  const [ruleName, setRuleName] = useState<string | null>(null);

  // Add states for remaining count popovers
  const [showTagsPopover, setShowTagsPopover] = useState(false);
  const [showGroupsPopover, setShowGroupsPopover] = useState(false);
  const [showCustomFieldsPopover, setShowCustomFieldsPopover] = useState(false);
  const [tagsPopoverPosition, setTagsPopoverPosition] = useState<{ top: number; left: number } | null>(null);
  const [groupsPopoverPosition, setGroupsPopoverPosition] = useState<{ top: number; left: number } | null>(null);
  const [customFieldsPopoverPosition, setCustomFieldsPopoverPosition] = useState<{ top: number; left: number } | null>(null);
  const tagsPopoverRef = useRef<HTMLDivElement>(null);
  const groupsPopoverRef = useRef<HTMLDivElement>(null);
  const customFieldsPopoverRef = useRef<HTMLDivElement>(null);
  const tagsHideTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const groupsHideTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const customFieldsHideTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const handleNodeClick = () => {
    onNodeClick?.(nodeId);
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowOptionsPopover(false);
    onDeleteNode?.(nodeId);
  };

  const handleOptionsClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowOptionsPopover(!showOptionsPopover);
  };

  // Tags popover handlers
  const handleShowTagsPopover = () => {
    if (tagsHideTimeoutRef.current) {
      clearTimeout(tagsHideTimeoutRef.current);
      tagsHideTimeoutRef.current = null;
    }

    if (tagsPopoverRef.current) {
      const rect = tagsPopoverRef.current.getBoundingClientRect();
      setTagsPopoverPosition({
        top: rect.bottom + window.scrollY + 8,
        left: rect.left + window.scrollX,
      });
      setShowTagsPopover(true);
    }
  };

  const handleHideTagsPopover = () => {
    tagsHideTimeoutRef.current = setTimeout(() => {
      setShowTagsPopover(false);
      setTagsPopoverPosition(null);
    }, 150);
  };

  const handleTagsPopoverMouseEnter = () => {
    if (tagsHideTimeoutRef.current) {
      clearTimeout(tagsHideTimeoutRef.current);
      tagsHideTimeoutRef.current = null;
    }
  };

  const handleTagsPopoverMouseLeave = () => {
    setShowTagsPopover(false);
    setTagsPopoverPosition(null);
  };

  // Groups popover handlers
  const handleShowGroupsPopover = () => {
    if (groupsHideTimeoutRef.current) {
      clearTimeout(groupsHideTimeoutRef.current);
      groupsHideTimeoutRef.current = null;
    }

    if (groupsPopoverRef.current) {
      const rect = groupsPopoverRef.current.getBoundingClientRect();
      setGroupsPopoverPosition({
        top: rect.bottom + window.scrollY + 8,
        left: rect.left + window.scrollX,
      });
      setShowGroupsPopover(true);
    }
  };

  const handleHideGroupsPopover = () => {
    groupsHideTimeoutRef.current = setTimeout(() => {
      setShowGroupsPopover(false);
      setGroupsPopoverPosition(null);
    }, 150);
  };

  const handleGroupsPopoverMouseEnter = () => {
    if (groupsHideTimeoutRef.current) {
      clearTimeout(groupsHideTimeoutRef.current);
      groupsHideTimeoutRef.current = null;
    }
  };

  const handleGroupsPopoverMouseLeave = () => {
    setShowGroupsPopover(false);
    setGroupsPopoverPosition(null);
  };

  // Custom fields popover handlers
  const handleShowCustomFieldsPopover = () => {
    if (customFieldsHideTimeoutRef.current) {
      clearTimeout(customFieldsHideTimeoutRef.current);
      customFieldsHideTimeoutRef.current = null;
    }

    if (customFieldsPopoverRef.current) {
      const rect = customFieldsPopoverRef.current.getBoundingClientRect();
      setCustomFieldsPopoverPosition({
        top: rect.bottom + window.scrollY + 8,
        left: rect.left + window.scrollX,
      });
      setShowCustomFieldsPopover(true);
    }
  };

  const handleHideCustomFieldsPopover = () => {
    customFieldsHideTimeoutRef.current = setTimeout(() => {
      setShowCustomFieldsPopover(false);
      setCustomFieldsPopoverPosition(null);
    }, 150);
  };

  const handleCustomFieldsPopoverMouseEnter = () => {
    if (customFieldsHideTimeoutRef.current) {
      clearTimeout(customFieldsHideTimeoutRef.current);
      customFieldsHideTimeoutRef.current = null;
    }
  };

  const handleCustomFieldsPopoverMouseLeave = () => {
    setShowCustomFieldsPopover(false);
    setCustomFieldsPopoverPosition(null);
  };

  const {
    data: {
      worklets: specWorklets = undefined,
    } = {},
  } = useGetAllWorkletsForParentSpecQuery({
    variables: {
      specKey: programData?.program?.specKey,
    },
    skip: !programData?.program?.specKey,
  });

  // Check if project has product catalog stage based on worklets API instead of workItems
  const doesProductCatalogStageExists =
    specWorklets?.some(
      (worklet: any) => worklet.specURI === WorkletSpecUri.SendBrandProductCatalogWorkletSpecification,
    ) || false;
  const isProductCatalogActionDisabled = !doesProductCatalogStageExists;

  // Create action items from actionOptions prop using consistent icons from nodeIconMapping
  const actionItems: ActionItem[] =
    actionOptions && actionOptions.length > 0
      ? actionOptions.flatMap((option): ActionItem[] => {
        let isDisabled = false;

        // Get icon config from nodeIconMapping for consistency
        const getIconForActionType = (actionType: AutomationActionNodeSubType) => {
          const config = getNodeIconConfig({ type: 'ACTION', subType: actionType });
          return {
            iconComponent: config.icon,
            bgColor: config.backgroundColor.replace('bg-', 'bg-'), // Keep the same format
          };
        };

        switch (option.type) {
          case AutomationActionNodeSubType.SEND_EMAIL: {
            const { iconComponent, bgColor } = getIconForActionType(option.type);
            return [
              {
                id: option.type,
                iconComponent,
                label: 'Send Email',
                bgColor,
                isDisabled,
              },
            ];
          }
          case AutomationActionNodeSubType.ADD_TO_OFFER: {
            const { iconComponent, bgColor } = getIconForActionType(option.type);
            return [
              {
                id: option.type,
                iconComponent,
                label: 'Add to Offer',
                bgColor,
                isDisabled,
              },
            ];
          }
          case AutomationActionNodeSubType.SEND_PRODUCT_CATALOG: {
            if (isStaOffer) {
              return [];
            }
            const { iconComponent, bgColor } = getIconForActionType(option.type);
            isDisabled = isProductCatalogActionDisabled;
            return [
              {
                id: option.type,
                iconComponent,
                label: 'Send Product Catalog',
                bgColor,
                isDisabled,
              },
            ];
          }
          case AutomationActionNodeSubType.UPDATE_MEMBER_DATA: {
            // Expand UPDATE_MEMBER_DATA into three sub-actions using consistent styling
            const { iconComponent, bgColor } = getIconForActionType(option.type);
            return [
              {
                id: 'ADD_TAG',
                iconComponent,
                label: 'Add Tag',
                bgColor,
                isDisabled: false,
                parentType: AutomationActionNodeSubType.UPDATE_MEMBER_DATA,
                memberDataType: 'ADD_TAG',
              },
              {
                id: 'ADD_TO_GROUP',
                iconComponent,
                label: 'Add to Group',
                bgColor,
                isDisabled: false,
                parentType: AutomationActionNodeSubType.UPDATE_MEMBER_DATA,
                memberDataType: 'ADD_TO_GROUP',
              },
              {
                id: 'UPDATE_MEMBER_INFORMATION',
                iconComponent,
                label: 'Update Custom Field',
                bgColor,
                isDisabled: false,
                parentType: AutomationActionNodeSubType.UPDATE_MEMBER_DATA,
                memberDataType: 'UPDATE_MEMBER_INFORMATION',
              },
            ];
          }
          case AutomationActionNodeSubType.HANDLE_PFA_BOOST: {
            const { iconComponent, bgColor } = getIconForActionType(option.type);
            return [
              {
                id: option.type,
                iconComponent,
                label: 'Apply Boost',
                bgColor,
                isDisabled,
              },
            ];
          }
          case AutomationActionNodeSubType.HANDLE_CASHBACK_BOOST: {
            const { iconComponent, bgColor } = getIconForActionType(option.type);
            return [
              {
                id: option.type,
                iconComponent,
                label: 'Cashback Boost',
                bgColor,
                isDisabled,
              },
            ];
          }
          default: {
            const config = getNodeIconConfig({ type: 'ACTION' });
            return [
              {
                id: option.type,
                iconComponent: config.icon,
                label: option.type,
                bgColor: config.backgroundColor,
                isDisabled,
              },
            ];
          }
        }
      })
      : [];

  const handleActionSelect = (selectedItemId: string) => {
    // Find the selected item to get its details
    const selectedItem: ActionItem | undefined = actionItems.find((item) => item.id === selectedItemId);

    if (!selectedItem) return;

    // Determine the actual action type and metadata
    const actualActionType = selectedItem.parentType || (selectedItemId as AutomationActionNodeSubType);
    // Get clean metadata for the selected action type
    const getCleanMetadataForActionType = (subType: AutomationActionNodeSubType, memberDataType?: string) => {
      const baseMetadata = {
        resourceId: 1,
        fromPreviousSender: false,
      };

      switch (subType) {
        case AutomationActionNodeSubType.SEND_EMAIL:
          return {
            ...baseMetadata,
            templateId: undefined,
          };
        case AutomationActionNodeSubType.HANDLE_PFA_BOOST:
          return {
            ...baseMetadata,
            boostId: undefined,
            brandCatalogId: undefined,
          };
        case AutomationActionNodeSubType.UPDATE_MEMBER_DATA:
          return {
            ...baseMetadata,
            type: memberDataType || '',
            fieldValue: '',
          };
        case AutomationActionNodeSubType.ADD_TO_OFFER:
          return {
            ...baseMetadata,
            offerId: undefined,
            selectedPayoutId: undefined,
            sendEmail: false,
          };
        case AutomationActionNodeSubType.SEND_PRODUCT_CATALOG:
          return {
            ...baseMetadata,
            brandCatalogId: undefined,
            sendEmail: false,
          };
        default:
          return {
            ...baseMetadata,
          };
      }
    };

    const cleanMetadata = getCleanMetadataForActionType(actualActionType, selectedItem.memberDataType);

    // Update the node to remove placeholder and set action type
    onUpdateNode?.(nodeId, {
      subType: actualActionType,
      metadata: cleanMetadata,
    });

    // Close the dropdown - users will interact with the configured node directly
    setIsActionSelectorOpen(false);
  };

  // Close popover when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(event.target as Node)) {
        setShowOptionsPopover(false);
      }
    };

    if (showOptionsPopover) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showOptionsPopover]);

  const actionSubType = subType as AutomationActionNodeSubType;
  const isActionPlaceholder = metadata?.isActionPlaceholder === true;
  const isConfigured =
    !isActionPlaceholder &&
    metadata &&
    ((actionSubType === AutomationActionNodeSubType.SEND_EMAIL &&
      metadata.templateId !== 1 &&
      (metadata.resourceId !== undefined || metadata.fromPreviousSender === true)) ||
      (actionSubType === AutomationActionNodeSubType.HANDLE_PFA_BOOST &&
        metadata.boostId &&
        metadata.resourceId !== undefined &&
        metadata.fromPreviousSender !== undefined) ||
      (actionSubType === AutomationActionNodeSubType.HANDLE_CASHBACK_BOOST &&
        metadata.resourceId !== undefined &&
        metadata.fromPreviousSender !== undefined) ||
      (actionSubType === AutomationActionNodeSubType.UPDATE_MEMBER_DATA &&
        metadata.type &&
        (metadata.fieldValue ||
          (metadata.values && metadata.values.length > 0) ||
          (metadata.type === UPDATE_MEMBER_DATA_TYPES.UPDATE_MEMBER_INFORMATION &&
            metadata.customFields &&
            metadata.customFields.length > 0))) ||
      (actionSubType === AutomationActionNodeSubType.ADD_TO_OFFER &&
        metadata.offerId &&
        metadata.resourceId !== undefined) ||
      (actionSubType === AutomationActionNodeSubType.SEND_PRODUCT_CATALOG && metadata.brandCatalogId));

  // Handle redirect to template in new tab for SEND_EMAIL actions
  const handleRedirectToTemplate = () => {
    if (actionSubType === AutomationActionNodeSubType.SEND_EMAIL && metadata?.templateId) {
      window.open(`/client/${clientInfo.id}/settings/messageTemplates?templateId=${metadata.templateId}`, '_blank');
    }
  };

  const handleRedirectToOffer = () => {
    if (actionSubType === AutomationActionNodeSubType.ADD_TO_OFFER && metadata?.offerId) {
      window.open(`/client/${clientInfo.id}/app/${SALES_APP_ID}/offers/${metadata.offerId}/details`, '_blank');
    }
  };

  // Get template details for SEND_EMAIL actions
  const getTemplateDetails = () => {
    if (actionSubType !== AutomationActionNodeSubType.SEND_EMAIL || !metadata?.templateId || !actionOptions?.length) {
      return { name: 'No template selected', subject: 'No subject available' };
    }

    const template = actionOptions[0]?.messageTemplates?.find((t) => t.id === metadata.templateId);
    return {
      name: template?.displayName || `Template ${metadata.templateId}`,
      subject: template?.emailSubject || 'No subject available',
    };
  };

  // Get sender info for SEND_EMAIL actions
  const getSenderInfo = () => {
    if (actionSubType !== AutomationActionNodeSubType.SEND_EMAIL) return '';

    if (metadata?.fromPreviousSender) {
      return 'Previous Sender';
    }

    if (metadata?.resourceId && actionOptions?.length > 0) {
      const resource = actionOptions[0]?.emailResources?.find((r) => r.id === metadata.resourceId);
      if (resource) {
        // EmailResourceData only has displayName, so we'll use that
        return resource.displayName || `Resource ${metadata.resourceId}`;
      }
    }
    return 'Select email address';
  };

  // Get email template details for actions with sendEmail enabled
  const getEmailNotificationDetails = () => {
    if (!metadata?.sendEmail || !metadata?.templateId || !actionOptions?.length) {
      return { name: 'No template selected', subject: 'No subject available', sender: 'Select email address' };
    }

    const template = actionOptions[0]?.messageTemplates?.find((t) => t.id === metadata.templateId);

    let sender = 'Select email address';
    if (metadata?.fromPreviousSender) {
      sender = 'Previous Sender';
    } else if (metadata?.resourceId && actionOptions?.length > 0) {
      const resource = actionOptions[0]?.emailResources?.find((r) => r.id === metadata.resourceId);
      if (resource) {
        sender = resource.displayName || `Resource ${metadata.resourceId}`;
      }
    }

    return {
      name: template?.displayName || `Template ${metadata.templateId}`,
      subject: template?.emailSubject || 'No subject available',
      sender,
    };
  };

  const getUpdateMemberDataDetails = () => {
    if (!metadata || !metadata.type) {
      return { action: 'No update configured', type: 'Type', value: 'Value' };
    }

    // Handle custom field updates - check both new unified format (values) and legacy format (customFields)
    if (metadata.type === UPDATE_MEMBER_DATA_TYPES.UPDATE_MEMBER_INFORMATION) {
      // Check new unified format first, then fall back to legacy format
      const customFieldsData = metadata.values || metadata.customFields;

      if (!customFieldsData || customFieldsData.length === 0) {
        return { action: 'No custom fields configured', type: 'Custom Fields', value: 'None' };
      }

      const fieldCount = customFieldsData.length;

      // Create a unique key for each field update to handle potential duplicates properly
      const fieldValues = customFieldsData.map((field: any, index: number) => {
        let displayValue = field.fieldValue;
        // Format value for display in tags
        if (field.fieldValue === true) {
          displayValue = 'Yes';
        } else if (field.fieldValue === false) {
          displayValue = 'No';
        } else if (Array.isArray(field.fieldValue)) {
          displayValue = field.fieldValue.join(', ');
        } else if (field.fieldValue instanceof Date) {
          displayValue = format(field.fieldValue, 'MMM do, yyyy');
        } else if (
          typeof field.fieldValue === 'string' &&
          field.fieldValue.includes('T') &&
          !isNaN(Date.parse(field.fieldValue))
        ) {
          // Handle ISO date strings
          displayValue = format(new Date(field.fieldValue), 'MMM do, yyyy');
        }

        // Create a meaningful tag that shows field name and value
        const tagText = `${displayValue}`;
        // If same field appears multiple times, add index to make it unique
        const duplicateCount = customFieldsData.filter(
          (f: any, i: number) => i <= index && f.fieldId === field.fieldId,
        ).length;

        if (duplicateCount > 1) {
          return `${tagText} (#${duplicateCount})`;
        }

        return tagText;
      });

      return {
        action: 'Update Custom Fields',
        type: 'Custom Fields',
        value: `${fieldCount} field${fieldCount === 1 ? '' : 's'} configured`,
        showTags: true,
        fieldValues,
        itemsToShow: Math.min(fieldValues.length, 2),
        remainingCount: Math.max(0, fieldValues.length - 2),
      };
    }

    // Handle traditional tag/group updates
    const isTagOperation = metadata.type === UPDATE_MEMBER_DATA_TYPES.ADD_TAG || metadata.type === 'TAG';
    const isGroupOperation = metadata.type === UPDATE_MEMBER_DATA_TYPES.ADD_TO_GROUP || metadata.type === 'GROUP';

    // Handle multi-tag format
    if (isTagOperation && metadata.values && metadata.values.length > 0) {
      const tagCount = metadata.values.length;
      const fieldValues = metadata.values.map((v: any) => v.fieldValue);
      return {
        action: '',
        type: 'Tags',
        value: `${tagCount} tag${tagCount > 1 ? 's' : ''} selected`,
        showTags: true,
        fieldValues,
        itemsToShow: Math.min(fieldValues.length, 2),
        remainingCount: Math.max(0, fieldValues.length - 2),
      };
    }

    // Handle multi-group format
    if (isGroupOperation && metadata.values && metadata.values.length > 0) {
      const groupCount = metadata.values.length;
      const fieldValues = metadata.values.map((v: any) => v.fieldValue);
      return {
        action: '',
        type: 'Groups',
        value: `${groupCount} group${groupCount > 1 ? 's' : ''} selected`,
        showTags: true,
        showGroups: true,
        fieldValues,
        itemsToShow: Math.min(fieldValues.length, 2),
        remainingCount: Math.max(0, fieldValues.length - 2),
      };
    }

    // Handle legacy single value format
    if (!metadata.fieldValue) {
      return { action: 'No update configured', type: 'Type', value: 'Value' };
    }

    const actionType = isTagOperation ? 'Tag' : 'Group';
    const actionVerb = isTagOperation ? 'Add tag' : 'Add to group';

    return {
      action: `${actionVerb}: ${metadata.fieldValue}`,
      type: actionType,
      value: metadata.fieldValue,
    };
  };

  // Fetch offer details when offerId is available
  useEffect(() => {
    if (metadata?.offerId && staApolloClient) {
      // First fetch basic offer info
      staApolloClient
        .query({
          query: GET_OFFERS_QUERY,
          fetchPolicy: 'network-only',
        })
        .then((response) => {
          const offer = response.data.offers?.find((o: GetOffers_offers) => o.id === metadata.offerId);
          if (offer) {
            setSelectedOffer(offer);
          }
        })
        .catch((err) => {
          logger.error('Error fetching offers:', err);
        });

      // Then fetch detailed offer info
      staApolloClient
        .query<GetofferByIdWithStats, GetofferByIdWithStatsVariables>({
          query: GET_OFFER_BY_ID_WITH_STATS_QUERY,
          variables: {
            id: metadata.offerId,
          },
          fetchPolicy: 'network-only',
        })
        .then((response) => {
          setSelectedOfferDetails(response.data.offer);

          // Get payout label using utility function
          if (metadata?.isAutomatedPayout) {
            setPayoutLabel('Automated Payout');
          } else if (metadata.selectedPayoutId && response.data.offer?.offer.payouts) {
            const selectedPayout = response.data.offer.offer.payouts.find(
              (payout) => payout.id === metadata.selectedPayoutId,
            );
            if (selectedPayout) {
              const label = getPayoutLabel(selectedPayout);
              setPayoutLabel(label || 'No Payout Label');
            } else {
              setPayoutLabel('No Payouts Selected');
            }
          } else {
            setPayoutLabel('No Payouts Available');
          }
        })
        .catch((err) => {
          logger.error('Error fetching offer details:', err);
        });
    }
  }, [metadata?.offerId, metadata?.selectedPayoutId, metadata?.isAutomatedPayout, staApolloClient]);

  // Fetch catalog and rule details for SEND_PRODUCT_CATALOG actions
  useEffect(() => {
    let cancelled = false;
    if (subType === AutomationActionNodeSubType.SEND_PRODUCT_CATALOG && pfaApolloClient) {
      // Fetch catalog name if brandCatalogId is available
      if (metadata?.brandCatalogId) {
        pfaApolloClient
          .query<GetBrandCatalogsQuery>({
            query: GET_BRAND_CATALOGS_QUERY,
          })
          .then((response) => {
            const catalog = response.data.getBrandCatalogs.find(
              (c) => String(c.id) === String(metadata.brandCatalogId),
            );
            if (!cancelled) setCatalogName(catalog?.name || null);
          })
          .catch((err) => {
            logger.error('Error fetching catalog details:', err);
            if (!cancelled) setCatalogName(null);
          });
      } else {
        setCatalogName(null);
      }

      // Fetch rule name if selectionRuleId is available
      if (metadata?.brandCatalogId && metadata?.selectionRuleId) {
        const brandCatalogIdNum = Number(metadata.brandCatalogId);
        if (!Number.isFinite(brandCatalogIdNum)) {
          logger.warn('Invalid brandCatalogId:', metadata.brandCatalogId);
          setRuleName(null);
        } else {
          pfaApolloClient
            .query<GetCatalogSelectionRuleByCatalogId, GetCatalogSelectionRuleByCatalogIdVariables>({
              query: GET_SELECTION_CRITERIA,
              variables: { brandCatalogId: brandCatalogIdNum },
            })
            .then((response) => {
              const rule = response.data.getCatalogSelectionRuleByCatalogId.find(
                (r) => String(r.id) === String(metadata.selectionRuleId),
              );
              const name = rule?.label || rule?.description || null;
              if (!cancelled) setRuleName(name);
              if (rule && !name) logger.warn('Rule found but missing name field:', rule);
              else if (!rule) logger.warn('No rule found with ID:', metadata.selectionRuleId);
            })
            .catch((err) => {
              logger.error('Error fetching rule details:', err);
              if (!cancelled) setRuleName(null);
            });
        }
      } else {
        setRuleName(null);
      }
    } else {
      // Reset states when not SEND_PRODUCT_CATALOG
      setCatalogName(null);
      setRuleName(null);
    }
    return () => {
      cancelled = true;
    };
  }, [subType, metadata?.brandCatalogId, metadata?.selectionRuleId, pfaApolloClient]);

  // Height measurement effect for dynamic spacing
  useLayoutEffect(() => {
    if (nodeRef.current && onNodeResize) {
      const measureHeight = () => {
        const height = nodeRef.current?.offsetHeight;
        if (height) {
          onNodeResize(nodeId, height);
        }
      };

      // Initial measurement
      measureHeight();

      // Create ResizeObserver for dynamic height changes
      const resizeObserver = new ResizeObserver(measureHeight);
      resizeObserver.observe(nodeRef.current);

      return () => {
        resizeObserver.disconnect();
      };
    }
  }, [nodeId, onNodeResize, isConfigured, subType, metadata, emailAccordionExpanded]); // Re-measure when content changes

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (tagsHideTimeoutRef.current) {
        clearTimeout(tagsHideTimeoutRef.current);
      }
      if (groupsHideTimeoutRef.current) {
        clearTimeout(groupsHideTimeoutRef.current);
      }
      if (customFieldsHideTimeoutRef.current) {
        clearTimeout(customFieldsHideTimeoutRef.current);
      }
    };
  }, []);

  return (
    <div ref={nodeRef} className="relative">
      {/* Input handle */}
      <Handle
        type="target"
        position={Position.Top}
        isConnectable={false}
        className="w-0 h-0 opacity-0"
      />

      {/* Main node container */}
      <div className="flex flex-col items-start justify-center relative w-[342px] transition-all duration-200">
        {/* Node with border */}
        <div
          className={cn(
            'w-full bg-white border border-grey-2 rounded-2xl transition-all duration-200 pb-4',
            isSelected && 'border-grey-6 border-[1px] shadow-md shadow-black/10',
          )}
        >
          {/* Header Card */}
          <div className="w-full bg-white cursor-pointer rounded-t-2xl" onClick={handleNodeClick}>
            <div className="flex h-12 items-center gap-1 p-3 relative w-full">
              <div className="flex justify-between flex-1 self-stretch grow items-center relative">
                <div className="inline-flex items-center gap-2 relative flex-[0_0_auto]">
                  <NodeIcon
                    node={{ type: AutomationNodeType.ACTION, subType: subType as AutomationActionNodeSubType }}
                    size="md"
                  />
                  <div className="relative w-fit font-medium text-grey-6 text-sm tracking-[0] leading-[22px] whitespace-nowrap">
                    {isActionPlaceholder
                      ? 'Select action to perform'
                      : getNodeDisplayLabel(
                        AutomationNodeType.ACTION,
                        subType as AutomationActionNodeSubType,
                        metadata,
                      )}
                  </div>
                </div>
                <div className="inline-flex gap-2 flex-[0_0_auto] items-center relative">
                  <div ref={popoverRef}>
                    <DeleteNodeButton handleDelete={handleDelete} showOptionsPopover={showOptionsPopover} setShowOptionsPopover={setShowOptionsPopover} handleOptionsClick={handleOptionsClick} isEditing={isEditing} />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Content Card */}
          <div className="w-full bg-white rounded-b-2xl overflow-hidden">
            <div className="flex flex-col items-start gap-3 px-3 pb-3 relative w-full bg-white">
              {isActionPlaceholder ? (
                // Show "Select Action" placeholder UI with dropdown
                <div className="w-full">
                  <Popover open={isActionSelectorOpen} onOpenChange={setIsActionSelectorOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className="w-full h-14 bg-grey-1 border-dashed border-grey-3 rounded-xl flex items-center justify-center gap-2 hover:bg-grey-1"
                        onClick={() => setIsActionSelectorOpen(true)}
                      >
                        <Sparkles className="h-3 w-3 text-grey-5" />
                        <span className="font-medium text-grey-5 text-sm">Select Action</span>
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent
                      className="w-[314px] p-0 bg-grey-0 rounded-2xl overflow-hidden border-0 shadow-lg"
                      align="center"
                      side="bottom"
                      sideOffset={8}
                    >
                      <Command className="bg-grey-0 rounded-2xl overflow-hidden border-0 shadow-none">
                        <div className="flex items-center gap-2 h-8 py-2 border border-grey-2 rounded-lg mx-2 mt-2">
                          {/* <Search className="w-4 h-4 text-grey-4" /> */}
                          <CommandInput
                            placeholder="Search Actions"
                            className="border-0 bg-transparent text-sm text-grey-4 placeholder:text-grey-4 focus:ring-0 focus:outline-none p-0 h-8"
                          />
                        </div>
                        <CommandList className="p-2 pt-2">
                          <CommandGroup className="p-0">
                            {actionItems.map((item: ActionItem, index: number) => {
                              const IconComponent = item.iconComponent;

                              if (!isStaOffer && item.isDisabled) {
                                return (
                                  <TooltipProvider key={index}>
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <div className="flex items-center justify-between w-full px-2 py-[5px] text-sm opacity-50 cursor-not-allowed rounded-lg" aria-disabled="true">
                                          <div className="flex items-center gap-2">
                                            <div
                                              className={`flex w-6 h-6 items-center justify-center rounded-lg ${item.bgColor}`}
                                            >
                                              <IconComponent className="w-4 h-4 text-grey-0" />
                                            </div>
                                            <span className="font-medium text-grey-6 text-sm">{item.label}</span>
                                            <AlertCircle className="h-3 w-3 text-yellow-500" />
                                          </div>
                                        </div>
                                      </TooltipTrigger>
                                      <TooltipContent>
                                        <p className="text-xs">
                                          For this action, Project must have Product Catalog stage
                                        </p>
                                      </TooltipContent>
                                    </Tooltip>
                                  </TooltipProvider>
                                );
                              }

                              return (
                                <CommandItem
                                  key={index}
                                  className={cn(
                                    'relative flex cursor-pointer select-none items-center gap-2 rounded-lg px-2 py-[5px] text-sm outline-none',
                                    'hover:bg-grey-2 data-[highlighted]:bg-grey-2 focus:bg-grey-2 data-[selected=true]:bg-grey-2'
                                  )}
                                  onSelect={() => handleActionSelect(item.id)}
                                >
                                  <div
                                    className={`flex w-6 h-6 items-center justify-center rounded-lg ${item.bgColor}`}
                                  >
                                    <IconComponent className="w-4 h-4 text-grey-0" />
                                  </div>
                                  <span className="font-medium text-grey-6 text-sm">{item.label}</span>
                                </CommandItem>
                              );
                            })}
                          </CommandGroup>
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                </div>
              ) : isConfigured ? (
                // Configured state - show details
                <div className="flex flex-col items-start justify-center gap-2 px-3 py-2 relative self-stretch w-full flex-[0_0_auto] bg-grey-1 rounded-xl overflow-hidden">
                  {actionSubType === AutomationActionNodeSubType.SEND_EMAIL ? (
                    // SEND_EMAIL specific UI
                    <div className="items-start gap-1 px-3 py-2 self-stretch w-full flex-[0_0_auto] bg-white rounded-xl border border-solid border-grey-2">
                      <div className="flex flex-col items-start gap-1 relative flex-1 grow p-0">
                        <div className="flex justify-between self-stretch w-full items-center relative flex-[0_0_auto]">
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <div className="relative w-fit font-medium text-grey-6 text-sm tracking-[0] leading-5 whitespace-nowrap overflow-hidden text-ellipsis max-w-[200px]">
                                  {getTemplateDetails().name}
                                </div>
                              </TooltipTrigger>
                              <TooltipContent className="bg-grey-0 text-grey-6 shadow-lg border border-gray-200 rounded-md px-3 py-2 font-medium z-50 max-w-[200px]">
                                {getTemplateDetails().name}
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                          <div className="inline-flex items-center gap-1 relative flex-[0_0_auto]">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="flex-col w-6 h-6 items-center justify-center gap-2 p-1 rounded-md"
                              onClick={handleRedirectToTemplate}
                            >
                              <FontAwesomeIcon icon={faArrowUpRightFromSquare} className="w-3 h-3 text-grey-5" />
                            </Button>
                            {isEditing && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="flex-col w-6 h-6 items-center justify-center gap-2 p-2 rounded-md"
                                onClick={handleNodeClick}
                              >
                                <FontAwesomeIcon icon={faPen} className="w-3 h-3 text-grey-5" />
                              </Button>
                            )}
                          </div>
                        </div>
                        <div className="relative self-stretch text-xs tracking-[0] leading-5 flex gap-1">
                          <span className="font-medium text-grey-6">Subject: </span>
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span className="text-grey-5 truncate inline-block max-w-[150px]">{getTemplateDetails().subject}</span>
                              </TooltipTrigger>
                              <TooltipContent className="bg-grey-0 text-grey-6 shadow-lg border border-gray-200 rounded-md px-3 py-2 font-medium z-50 max-w-[200px]">
                                {getTemplateDetails().subject}
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </div>
                      </div>
                    </div>
                  ) : actionSubType === AutomationActionNodeSubType.UPDATE_MEMBER_DATA ? (
                    // UPDATE_MEMBER_DATA specific UI
                    <div className="items-start gap-1 w-full">
                      <div className="flex flex-col items-start gap-1 relative flex-1 grow p-0">
                        <div className="flex justify-between self-stretch w-full items-start relative flex-[0_0_auto]">
                          <div className="relative flex-1 font-medium text-grey-6 text-sm tracking-[0] leading-5">
                            {(() => {
                              const details = getUpdateMemberDataDetails();
                              if (!details.showTags || !details.fieldValues) {
                                // Fallback to traditional display
                                return (
                                  <div className="relative self-stretch text-xs tracking-[0] leading-5">
                                    <span className="font-medium text-grey-6">{details.type}: </span>
                                    <span className="text-grey-5">{details.value}</span>
                                  </div>
                                );
                              }

                              return (
                                <div className="flex flex-wrap gap-1">
                                  {details.showGroups
                                    ? // Show groups with grey styling (first 2 + remaining count)
                                    [
                                      ...details.fieldValues.slice(0, details.itemsToShow).map((value: string, index: number) => (
                                        <div
                                          key={index}
                                          className="flex items-center gap-1 px-2 py-1 rounded-md bg-grey-2 text-grey-6 border-none"
                                        >
                                          <span className="text-xs font-medium">{value}</span>
                                        </div>
                                      )),
                                      ...(details.remainingCount > 0 ? [
                                        <div
                                          key="groups-remaining"
                                          className="relative"
                                          ref={groupsPopoverRef}
                                          onMouseEnter={handleShowGroupsPopover}
                                          onMouseLeave={handleHideGroupsPopover}
                                        >
                                          <div className="flex items-center gap-1 px-2 py-1 rounded-md bg-grey-2 text-grey-5 border-none hover:bg-grey-3 transition-colors cursor-pointer">
                                            <span className="text-xs font-medium">+{details.remainingCount} more</span>
                                          </div>
                                        </div>
                                      ] : [])
                                    ]
                                    : metadata?.type === UPDATE_MEMBER_DATA_TYPES.UPDATE_MEMBER_INFORMATION
                                      ? // Show custom fields with grey styling (first 2 + remaining count)
                                      [
                                        ...details.fieldValues.slice(0, details.itemsToShow).map((value: string, index: number) => (
                                          <div
                                            key={index}
                                            className="flex items-center gap-1 px-2 py-1 rounded-md bg-grey-2 text-grey-6 border-none"
                                          >
                                            <span className="text-xs font-medium">{value}</span>
                                          </div>
                                        )),
                                        ...(details.remainingCount > 0 ? [
                                          <div
                                            key="custom-fields-remaining"
                                            className="relative"
                                            ref={customFieldsPopoverRef}
                                            onMouseEnter={handleShowCustomFieldsPopover}
                                            onMouseLeave={handleHideCustomFieldsPopover}
                                          >
                                            <div className="flex items-center gap-1 px-2 py-1 rounded-md bg-grey-2 text-grey-5 border-none hover:bg-grey-3 transition-colors cursor-pointer">
                                              <span className="text-xs font-medium">+{details.remainingCount} more</span>
                                            </div>
                                          </div>
                                        ] : [])
                                      ]
                                      : // Show tags with colored backgrounds (first 2 + remaining count)
                                      [
                                        ...details.fieldValues.slice(0, details.itemsToShow).map((value: string, index: number) => (
                                          <Badge
                                            key={index}
                                            variant="secondary"
                                            className="text-xs px-2 py-1 rounded-md border-0"
                                            style={{
                                              backgroundColor:
                                                tagColors[simpleHash(value) % tagColors.length]?.backgroundColor ||
                                                '#e5e7eb',
                                              color: '#374151',
                                            }}
                                            title={value}
                                          >
                                            <span className="truncate max-w-[120px]">
                                              {value}
                                            </span>
                                          </Badge>
                                        )),
                                        ...(details.remainingCount > 0 ? [
                                          <div
                                            key="tags-remaining"
                                            className="relative"
                                            ref={tagsPopoverRef}
                                            onMouseEnter={handleShowTagsPopover}
                                            onMouseLeave={handleHideTagsPopover}
                                          >
                                            <Badge
                                              variant="secondary"
                                              className="text-xs px-2 py-1 rounded-md border-0 hover:bg-grey-3 transition-colors cursor-pointer bg-grey-2 text-grey-5"
                                            >
                                              +{details.remainingCount} more
                                            </Badge>
                                          </div>
                                        ] : [])
                                      ]}
                                </div>
                              );
                            })()}
                          </div>
                          {isEditing && (
                            <div className="inline-flex items-center gap-1 relative flex-[0_0_auto]">
                              <Button
                                variant="ghost"
                                size="sm"
                                className="flex-col w-6 h-6 items-center justify-center gap-2 p-2 rounded-md"
                                onClick={handleNodeClick}
                              >
                                <FontAwesomeIcon icon={faPen} className="w-3 h-3 text-grey-5" />
                              </Button>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ) : actionSubType === AutomationActionNodeSubType.ADD_TO_OFFER ? (
                    // ADD_TO_OFFER specific UI with Card-based layout to prevent overflow
                    <Card className="w-full max-w-full bg-grey-0 rounded-2xl border border-solid border-grey-2 overflow-hidden">
                      <CardContent className="flex flex-col items-start gap-2 px-3 py-2">
                        {selectedOffer && selectedOfferDetails ? (
                          // Rich offer preview
                          <>
                            <div className="flex items-center gap-2 relative self-stretch w-full min-w-0">
                              <div
                                className={`w-10 h-10 rounded-md flex items-center justify-center flex-shrink-0 ${getOfferIconData(selectedOffer).bgColor}`}
                              >
                                <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                                  {getOfferIconData(selectedOffer).iconType === 'link' ? (
                                    <FontAwesomeIcon icon={faLink} className="w-5 h-5 text-white flex-shrink-0" />
                                  ) : (
                                    <FontAwesomeIcon icon={faTags} className="w-5 h-5 text-white flex-shrink-0" />
                                  )}
                                </div>
                              </div>
                              <div className="flex flex-col items-start relative flex-1 min-w-0 overflow-hidden">
                                <div className="flex justify-between self-stretch w-full items-center relative flex-[0_0_auto] min-w-0">
                                  <div className="relative font-normal text-grey-6 text-xs tracking-[0] leading-5 min-w-0 overflow-hidden" style={{ maxWidth: 'calc(100% - 60px)' }} title={selectedOffer.name}>
                                    <span className="truncate block">
                                      {selectedOffer.name}
                                    </span>
                                  </div>
                                  <div className="inline-flex items-center gap-1 relative flex-[0_0_auto] flex-shrink-0 min-w-[50px]">
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      className="flex-col w-6 h-6 items-center justify-center gap-2 p-1 rounded-md flex-shrink-0"
                                      onClick={handleRedirectToOffer}
                                    >
                                      <FontAwesomeIcon icon={faArrowUpRightFromSquare} className="w-3 h-3 text-grey-5" />
                                    </Button>
                                    {isEditing && (
                                      <Button
                                        variant="ghost"
                                        size="icon"
                                        className="flex-col w-6 h-6 items-center justify-center gap-2 p-1 rounded-md flex-shrink-0"
                                        onClick={handleNodeClick}
                                      >
                                        <FontAwesomeIcon icon={faPen} className="w-3 h-3 text-grey-5" />
                                      </Button>
                                    )}
                                  </div>
                                </div>
                                <div className="flex items-center gap-1">
                                  <div className="relative self-stretch text-sm tracking-[0] leading-6 font-medium text-grey-6 truncate">
                                    {getOfferDiscountAmount(selectedOfferDetails)}
                                  </div>
                                  <Badge className="bg-green-2 text-green-7 text-xs px-2 shadow-none py-0.5 rounded-lg hover:bg-green-1 flex-shrink-0 whitespace-nowrap">
                                    {selectedOffer.expired ? 'Expired' : 'Active'}
                                  </Badge>
                                </div>
                              </div>
                            </div>

                            {payoutLabel && (
                              <div className="relative self-stretch text-xs tracking-[0] leading-5 truncate">
                                <span className="font-medium text-grey-6">Payout: </span>
                                <span className="text-grey-5">{payoutLabel}</span>
                              </div>
                            )}

                            {/* Email notification accordion for ADD_TO_OFFER */}
                            {metadata?.sendEmail && (
                              <div className="w-full border-t border-grey-2 pt-2 mt-2">
                                <div
                                  className="flex items-center gap-2 cursor-pointer rounded-md"
                                  onClick={() => setEmailAccordionExpanded(!emailAccordionExpanded)}
                                >
                                  <div className="w-4 h-4 rounded-full flex items-center justify-center bg-teal-5">
                                    <FontAwesomeIcon icon={faCheck} className="w-2 h-2 text-white" />
                                  </div>
                                  <span className="text-xs text-grey-6 flex-1">Send Email to notify members</span>
                                  {emailAccordionExpanded ? (
                                    <ChevronDown className="w-3 h-3 text-grey-5" />
                                  ) : (
                                    <ChevronRight className="w-3 h-3 text-grey-5" />
                                  )}
                                </div>
                                {emailAccordionExpanded && (
                                  <>
                                    <Card className="mt-2 bg-grey-0 border-grey-2">
                                      <CardContent className="p-3">
                                        <div className="flex flex-col gap-1">
                                          <div className="flex justify-between items-center">
                                            <div className="font-medium text-grey-6 text-sm">
                                              {getEmailNotificationDetails().name}
                                            </div>
                                            <div className="flex items-center gap-1">
                                              <Button
                                                variant="ghost"
                                                size="icon"
                                                className="h-6 w-6 p-0"
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  if (metadata?.templateId) {
                                                    window.open(`/client/${clientInfo.id}/settings/messageTemplates?templateId=${metadata.templateId}`, '_blank');
                                                  }
                                                }}
                                              >
                                                <FontAwesomeIcon icon={faArrowUpRightFromSquare} className="h-3 w-3 text-grey-5" />
                                              </Button>
                                              <Button
                                                variant="ghost"
                                                size="icon"
                                                className="h-6 w-6 p-0"
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  handleNodeClick();
                                                }}
                                              >
                                                <FontAwesomeIcon icon={faPen} className="h-3 w-3 text-grey-5" />
                                              </Button>
                                            </div>
                                          </div>
                                          <div className="text-xs leading-5">
                                            <span className="font-medium text-grey-6">Subject:</span>
                                            <span className="text-grey-5 truncate max-w-[150px]"> {getEmailNotificationDetails().subject}</span>
                                          </div>
                                        </div>
                                      </CardContent>
                                    </Card>
                                    <div className="text-xs text-grey-6 mt-2">
                                      <span className="font-medium">Send from:</span>
                                      <span className="text-grey-5 truncate max-w-[150px]"> {getEmailNotificationDetails().sender}</span>
                                    </div>
                                  </>
                                )}
                              </div>
                            )}
                          </>
                        ) : (
                          // Loading or basic fallback
                          <>
                            <div className="flex justify-between self-stretch w-full items-center relative flex-[0_0_auto] min-w-0 text-grey-5">
                              <div>Loading Offer...</div> <Spinner size="small" />
                            </div>
                            <div className="relative self-stretch text-xs tracking-[0] leading-5 truncate">
                              <span className="font-medium text-grey-6">Action: </span>
                              <span className="text-grey-5">Add member to selected offer</span>
                            </div>

                          </>
                        )}
                      </CardContent>
                    </Card>
                  ) : actionSubType === AutomationActionNodeSubType.SEND_PRODUCT_CATALOG ? (
                    // SEND_PRODUCT_CATALOG specific UI
                    <div className="items-start gap-1 px-3 py-2 self-stretch w-full flex-[0_0_auto] bg-white rounded-xl border border-solid border-grey-2">
                      <div className="flex flex-col items-start gap-2 relative flex-1 grow p-0">
                        <div className="flex justify-between self-stretch w-full items-center relative flex-[0_0_auto]">
                          <div className="relative w-fit font-medium text-grey-6 text-sm tracking-[0] leading-5 whitespace-nowrap overflow-hidden text-ellipsis max-w-[200px]">
                            {catalogName ||
                              (metadata?.brandCatalogId ? `Catalog ${metadata.brandCatalogId}` : 'Default Catalog')}
                          </div>
                          <div className="inline-flex items-center gap-1 relative flex-[0_0_auto]">
                            <Badge className="bg-green-1 text-green-7 text-xs px-2 shadow-none py-0.5 rounded-md hover:bg-green-2">
                              Active
                            </Badge>
                            <Button variant="ghost" size="sm" onClick={handleNodeClick}>
                              <FontAwesomeIcon icon={faPen} className="w-3 h-3 text-grey-5" />
                            </Button>
                          </div>
                        </div>
                        {metadata?.selectionRuleId !== null && metadata?.selectionRuleId !== undefined && (
                          <div className="relative self-stretch text-xs tracking-[0] leading-5">
                            <span className="font-medium text-grey-6">Selection Rule: </span>
                            <span className="text-grey-5">{ruleName || `Rule ID: ${metadata.selectionRuleId}`}</span>
                          </div>
                        )}
                        {metadata?.sendEmail && (
                          <div className="w-full">
                            <div
                              className="flex items-center gap-2 cursor-pointer rounded-md"
                              onClick={() => setEmailAccordionExpanded(!emailAccordionExpanded)}
                            >
                              <div className="w-4 h-4 rounded-full flex items-center justify-center bg-teal-5">
                                <FontAwesomeIcon icon={faCheck} className="w-2 h-2 text-white" />
                              </div>
                              <span className="text-xs text-grey-6 flex-1">Send Email to notify members</span>
                              {emailAccordionExpanded ? (
                                <ChevronDown className="w-3 h-3 text-grey-5" />
                              ) : (
                                <ChevronRight className="w-3 h-3 text-grey-5" />
                              )}
                            </div>
                            {emailAccordionExpanded && (
                              <>
                                <Card className="mt-2 bg-grey-0 border-grey-2">
                                  <CardContent className="p-3">
                                    <div className="flex flex-col gap-1">
                                      <div className="flex justify-between items-center">
                                        <div className="font-medium text-grey-6 text-sm">
                                          {getEmailNotificationDetails().name}
                                        </div>
                                        <div className="flex items-center gap-1">
                                          <Button
                                            variant="ghost"
                                            size="icon"
                                            className="h-6 w-6 p-0"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              if (metadata?.templateId) {
                                                window.open(`/client/${clientInfo.id}/settings/messageTemplates?templateId=${metadata.templateId}`, '_blank');
                                              }
                                            }}
                                          >
                                            <FontAwesomeIcon icon={faArrowUpRightFromSquare} className="h-3 w-3 text-grey-5" />
                                          </Button>
                                          <Button
                                            variant="ghost"
                                            size="icon"
                                            className="h-6 w-6 p-0"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handleNodeClick();
                                            }}
                                          >
                                            <FontAwesomeIcon icon={faPen} className="h-3 w-3 text-grey-5" />
                                          </Button>
                                        </div>
                                      </div>
                                      <div className="text-xs leading-5">
                                        <span className="font-medium text-grey-6">Subject:</span>
                                        <span className="text-grey-5"> {getEmailNotificationDetails().subject}</span>
                                      </div>
                                    </div>
                                  </CardContent>
                                </Card>
                                <div className="text-xs text-grey-6 mt-2">
                                  <span className="font-medium">Send from:</span>
                                  <span className="text-grey-5"> {getEmailNotificationDetails().sender}</span>
                                </div>
                              </>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  ) : (
                    // Other action types - use existing logic
                    <div className="bg-green-50 rounded-lg px-3 py-2">
                      <span className="text-sm text-green-700 font-medium">Configured</span>
                      <p className="text-xs text-green-600 mt-1">
                        {getActionDescription(subType as AutomationActionNodeSubType, metadata, actionOptions)}
                      </p>
                      {actionSubType === AutomationActionNodeSubType.HANDLE_PFA_BOOST && metadata?.boostId && (
                        <div className="bg-grey-1 rounded-lg px-3 py-2 mt-2">
                          <p className="text-xs text-grey-5">Boost ID: {metadata.boostId}</p>
                        </div>
                      )}
                    </div>
                  )}

                  {/* SEND_EMAIL specific sender info */}
                  {actionSubType === AutomationActionNodeSubType.SEND_EMAIL && (
                    <div className="relative w-fit text-xs tracking-[0] leading-3">
                      <span className="font-medium text-grey-6">Send from: </span>
                      <span className="text-grey-5">{getSenderInfo()}</span>
                    </div>
                  )}
                </div>
              ) : (
                // Not configured state
                <EmptyStateButton
                  nodeType={AutomationNodeType.ACTION}
                  actionSubType={actionSubType}
                  onClick={handleNodeClick}
                  metadata={metadata}
                />
              )}
            </div>
          </div>
        </div>

        {/* Bottom connector with add button - positioned like DelayNodeV2 */}
        {isEditing && (
          <AddNodeButton data={data} nodeId={nodeId} />
        )}
      </div>

      {/* Output handle */}
      <Handle
        type="source"
        position={Position.Bottom}
        isConnectable={false}
        className="w-0 h-0 opacity-0"
      />

      {/* Node Toolbar */}
      {data.isToolbarVisible && (
        <NodeToolbarV2
          nodeId={nodeId}
          isVisible
          onAddNode={data.onAddChild}
          onClose={() => {
            data.onCloseToolbar?.();
            data.onShowDashedPlaceholder?.(null);
          }}
          position={Position.Right}
          offset={15}
        />
      )}

      {/* Portal-based popovers for additional tags/groups/custom fields */}
      {/* Tags Popover */}
      {showTagsPopover && tagsPopoverPosition && (() => {
        const details = getUpdateMemberDataDetails();
        if (!details.showTags || !details.fieldValues || details.remainingCount === 0 || details.showGroups || metadata?.type === UPDATE_MEMBER_DATA_TYPES.UPDATE_MEMBER_INFORMATION) return null;

        return createPortal(
          <div
            className="fixed bg-white border border-gray-200 rounded-xl shadow-lg p-3 min-w-[200px] max-w-sm user-select-text pointer-events-auto"
            style={{
              top: tagsPopoverPosition.top,
              left: tagsPopoverPosition.left,
              zIndex: 999999,
            }}
            onMouseEnter={handleTagsPopoverMouseEnter}
            onMouseLeave={handleTagsPopoverMouseLeave}
          >
            <div className="flex flex-col gap-2">
              <div className="text-xs text-grey-5 font-medium mb-1">
                Additional Tags ({details.remainingCount})
              </div>
              <div
                className="flex flex-wrap gap-1.5 max-h-48 overflow-y-auto"
                style={{
                  WebkitOverflowScrolling: 'touch',
                }}
                onWheel={(e) => {
                  e.stopPropagation();
                }}
              >
                {details.fieldValues.slice(2).map((value: string, index: number) => (
                  <Badge
                    key={index + 2}
                    variant="secondary"
                    className="text-xs px-2 py-1 rounded-md border-0"
                    style={{
                      backgroundColor: tagColors[simpleHash(value) % tagColors.length]?.backgroundColor || '#e5e7eb',
                      color: '#374151',
                    }}
                    title={value}
                  >
                    <span className="truncate max-w-[150px]">
                      {value}
                    </span>
                  </Badge>
                ))}
              </div>
            </div>
          </div>,
          document.body
        );
      })()}

      {/* Groups Popover */}
      {showGroupsPopover && groupsPopoverPosition && (() => {
        const details = getUpdateMemberDataDetails();
        if (!details.showGroups || !details.fieldValues || details.remainingCount === 0) return null;

        return createPortal(
          <div
            className="fixed bg-white border border-gray-200 rounded-xl shadow-lg p-3 min-w-[200px] max-w-sm user-select-text pointer-events-auto"
            style={{
              top: groupsPopoverPosition.top,
              left: groupsPopoverPosition.left,
              zIndex: 999999,
            }}
            onMouseEnter={handleGroupsPopoverMouseEnter}
            onMouseLeave={handleGroupsPopoverMouseLeave}
          >
            <div className="flex flex-col gap-2">
              <div className="text-xs text-grey-5 font-medium mb-1">
                Additional Groups ({details.remainingCount})
              </div>
              <div
                className="flex flex-wrap gap-1.5 max-h-48 overflow-y-auto"
                style={{
                  WebkitOverflowScrolling: 'touch',
                }}
                onWheel={(e) => {
                  e.stopPropagation();
                }}
              >
                {details.fieldValues.slice(2).map((value: string, index: number) => (
                  <div
                    key={index + 2}
                    className="flex items-center gap-1 px-2 py-1 rounded-md bg-grey-2 text-grey-5 border-none"
                  >
                    <span className="text-xs font-medium">{value}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>,
          document.body
        );
      })()}

      {/* Custom Fields Popover */}
      {showCustomFieldsPopover && customFieldsPopoverPosition && (() => {
        const details = getUpdateMemberDataDetails();
        if (!details.showTags || !details.fieldValues || details.remainingCount === 0 || metadata?.type !== UPDATE_MEMBER_DATA_TYPES.UPDATE_MEMBER_INFORMATION) return null;

        return createPortal(
          <div
            className="fixed bg-white border border-gray-200 rounded-xl shadow-lg p-3 min-w-[200px] max-w-sm user-select-text pointer-events-auto"
            style={{
              top: customFieldsPopoverPosition.top,
              left: customFieldsPopoverPosition.left,
              zIndex: 999999,
            }}
            onMouseEnter={handleCustomFieldsPopoverMouseEnter}
            onMouseLeave={handleCustomFieldsPopoverMouseLeave}
          >
            <div className="flex flex-col gap-2">
              <div className="text-xs text-grey-5 font-medium mb-1">
                Additional Custom Fields ({details.remainingCount})
              </div>
              <div
                className="flex flex-wrap gap-1.5 max-h-48 overflow-y-auto"
                style={{
                  WebkitOverflowScrolling: 'touch',
                }}
                onWheel={(e) => {
                  e.stopPropagation();
                }}
              >
                {details.fieldValues.slice(2).map((value: string, index: number) => (
                  <div
                    key={index + 2}
                    className="flex items-center gap-1 px-2 py-1 rounded-md bg-grey-2 text-grey-5 border-none"
                  >
                    <span className="text-xs font-medium">{value}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>,
          document.body
        );
      })()}
    </div>
  );
};
